import { ChainId, Fraction, Token, WNATIVE } from '@nominex/nomiswap-sdk'
import { BUSD, DAI, NMX, USDC, USDT } from 'config/tokens'

export const REFUNDABLE_FRACTION = new Fraction('7', '10')
export const LP_FEE_FRACTION = new Fraction('3', '10')

// a list of tokens by chain
export type ChainMap<T> = {
  readonly [chainId in ChainId]: T
}

export type ChainTokenList = ChainMap<Token[]>

// used to construct intermediary pairs for trading
export const BASES_TO_CHECK_TRADES_AGAINST: ChainTokenList = {
  [ChainId.BSC]: [NMX[ChainId.BSC], BUSD[ChainId.BSC], USDC[ChainId.BSC], USDT[ChainId.BSC], WNATIVE[ChainId.BSC]],
  [ChainId.ETHEREUM]: [
    DAI[ChainId.ETHEREUM],
    USDC[ChainId.ETHEREUM],
    USDT[ChainId.ETHEREUM],
    WNATIVE[ChainId.ETHEREUM],
  ],
  [ChainId.MATIC]: [USDC[ChainId.MATIC], USDT[ChainId.MATIC], WNATIVE[ChainId.MATIC]],
  [ChainId.OPTIMISM]: [USDC[ChainId.OPTIMISM], USDT[ChainId.OPTIMISM], WNATIVE[ChainId.OPTIMISM]],
  [ChainId.GNOSIS]: [USDC[ChainId.GNOSIS], USDT[ChainId.GNOSIS], WNATIVE[ChainId.GNOSIS]],
  [ChainId.ARBITRUM]: [USDC[ChainId.ARBITRUM], USDT[ChainId.ARBITRUM], WNATIVE[ChainId.ARBITRUM]],
  [ChainId.AVALANCHE]: [USDC[ChainId.AVALANCHE], USDT[ChainId.AVALANCHE], WNATIVE[ChainId.AVALANCHE]],
}

/**
 * Additional bases for specific tokens
 * @example { [WBTC.address]: [renBTC], [renBTC.address]: [WBTC] }
 */
export const ADDITIONAL_BASES: ChainMap<{ [tokenAddress: string]: Token[] }> = {
  [ChainId.BSC]: {},
  [ChainId.ETHEREUM]: {},
  [ChainId.MATIC]: {},
  [ChainId.OPTIMISM]: {},
  [ChainId.GNOSIS]: {},
  [ChainId.ARBITRUM]: {},
  [ChainId.AVALANCHE]: {},
}

/**
 * Some tokens can only be swapped via certain pairs, so we override the list of bases that are considered for these
 * tokens.
 * @example [AMPL.address]: [DAI, WETH[ChainId.BSC]]
 */
export const CUSTOM_BASES: ChainMap<{ [tokenAddress: string]: Token[] }> = {
  [ChainId.BSC]: {},
  [ChainId.ETHEREUM]: {},
  [ChainId.MATIC]: {},
  [ChainId.OPTIMISM]: {},
  [ChainId.GNOSIS]: {},
  [ChainId.ARBITRUM]: {},
  [ChainId.AVALANCHE]: {},
}

// used for display in the default list when adding liquidity
export const SUGGESTED_BASES: ChainTokenList = {
  [ChainId.BSC]: [NMX[ChainId.BSC], BUSD[ChainId.BSC], USDC[ChainId.BSC], USDT[ChainId.BSC]],
  [ChainId.ETHEREUM]: [DAI[ChainId.ETHEREUM], USDC[ChainId.ETHEREUM], USDT[ChainId.ETHEREUM]],
  [ChainId.MATIC]: [],
  [ChainId.OPTIMISM]: [],
  [ChainId.GNOSIS]: [],
  [ChainId.ARBITRUM]: [],
  [ChainId.AVALANCHE]: [],
}

// used to construct the list of all pairs we consider by default in the frontend
export const BASES_TO_TRACK_LIQUIDITY_FOR: ChainTokenList = {
  [ChainId.BSC]: [NMX[ChainId.BSC], BUSD[ChainId.BSC], USDC[ChainId.BSC], USDT[ChainId.BSC], WNATIVE[ChainId.BSC]],
  [ChainId.ETHEREUM]: [
    DAI[ChainId.ETHEREUM],
    USDC[ChainId.ETHEREUM],
    USDT[ChainId.ETHEREUM],
    WNATIVE[ChainId.ETHEREUM],
  ],
  [ChainId.MATIC]: [USDC[ChainId.MATIC], USDT[ChainId.MATIC], WNATIVE[ChainId.MATIC]],
  [ChainId.OPTIMISM]: [USDC[ChainId.OPTIMISM], USDT[ChainId.OPTIMISM], WNATIVE[ChainId.OPTIMISM]],
  [ChainId.GNOSIS]: [USDC[ChainId.GNOSIS], USDT[ChainId.GNOSIS], WNATIVE[ChainId.GNOSIS]],
  [ChainId.ARBITRUM]: [USDC[ChainId.ARBITRUM], USDT[ChainId.ARBITRUM], WNATIVE[ChainId.ARBITRUM]],
  [ChainId.AVALANCHE]: [USDC[ChainId.AVALANCHE], USDT[ChainId.AVALANCHE], WNATIVE[ChainId.AVALANCHE]],
}

export const PINNED_PAIRS: ChainMap<[Token, Token][]> = {
  [ChainId.BSC]: [
    [NMX[ChainId.BSC], USDT[ChainId.BSC]],
    [NMX[ChainId.BSC], BUSD[ChainId.BSC]],
    [NMX[ChainId.BSC], USDC[ChainId.BSC]],
    [NMX[ChainId.BSC], WNATIVE[ChainId.BSC]],
  ],
  [ChainId.ETHEREUM]: [],
  [ChainId.MATIC]: [],
  [ChainId.OPTIMISM]: [],
  [ChainId.GNOSIS]: [],
  [ChainId.ARBITRUM]: [],
  [ChainId.AVALANCHE]: [],
}

export const QUOTE_TOKENS: ChainTokenList = {
  [ChainId.BSC]: [BUSD[ChainId.BSC], USDC[ChainId.BSC], USDT[ChainId.BSC]],
  [ChainId.ETHEREUM]: [DAI[ChainId.ETHEREUM], USDC[ChainId.ETHEREUM], USDT[ChainId.ETHEREUM]],
  [ChainId.MATIC]: [USDC[ChainId.MATIC], USDT[ChainId.MATIC]],
  [ChainId.OPTIMISM]: [],
  [ChainId.GNOSIS]: [],
  [ChainId.ARBITRUM]: [],
  [ChainId.AVALANCHE]: [],
}

export const STABLE_SWAP_PAIRS: ChainMap<[Token, Token][]> = {
  [ChainId.BSC]: [
    [USDC[ChainId.BSC], USDT[ChainId.BSC]],
    [BUSD[ChainId.BSC], USDT[ChainId.BSC]],
    [BUSD[ChainId.BSC], USDC[ChainId.BSC]],
  ],
  [ChainId.ETHEREUM]: [
    [USDC[ChainId.ETHEREUM], USDT[ChainId.ETHEREUM]],
    [DAI[ChainId.ETHEREUM], USDT[ChainId.ETHEREUM]],
  ],
  [ChainId.MATIC]: [[USDC[ChainId.MATIC], USDT[ChainId.MATIC]]],
  [ChainId.OPTIMISM]: [],
  [ChainId.GNOSIS]: [],
  [ChainId.ARBITRUM]: [],
  [ChainId.AVALANCHE]: [],
}
